import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingConfigEdits } from '../../hooks/usePendingConfigEdits';
import { useUpdateBillingConfigsBulk } from '../../hooks/api/budgetBillingConfig';
import { useDispatchErrorNotification } from '../../hooks';
import { queryClient } from '../../services/queryClient';
import { QueryKey } from '../../enums/QueryKey';
import { firstConfigEditIssue } from './configEditIssues';
import FormSaveActions from './FormSaveActions';
import CellLegend, { LegendCue } from './CellLegend';

interface ConfigSaveBarProps {
    pending: PendingConfigEdits;
    /** Config id → a human label for it (partner/location/working unit), so a blocking edit can be
     *  named even when the page's filters are not showing that row. */
    rowLabels?: Map<number, string>;
    /** Which cell cues this page paints — the two pages sharing this bar do not paint the same set:
     *  only the rate grids (Prices & ABT) have extrapolated cells. */
    legendCues?: LegendCue[];
}

// The save half of a Budget grid page (CALL-779): the sticky bar and the batch request behind it. Both
// pages that edit billing configs — Billing Config and Prices & ABT — mount exactly this, so the two
// cannot drift on what Save means.
//
// Failure keeps the pending set: the mutation toasts the server's message (see useUpdateRecord) and only
// the rows the response says were written are cleared, so a rejected batch leaves every edit on screen,
// still marked, ready to retry — which is the acceptance criterion the old per-cell autosave could not
// meet, because it had nowhere to put an edit whose request had failed.
const ConfigSaveBar = ({ pending, rowLabels, legendCues = ['editable', 'unsaved'] }: ConfigSaveBarProps) => {
    const { t } = useTranslation('budget');
    const { mutateAsync: saveAll } = useUpdateBillingConfigsBulk();
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    // Owned here rather than taken from the mutation: Save is not finished until the refetch that follows
    // it has landed (see handleSave), and the mutation's own isLoading ends before that.
    const [isSaving, setIsSaving] = useState(false);

    const issue = useMemo(() => firstConfigEditIssue(pending.edits, t), [pending.edits, t]);

    // Named with the row it belongs to: the store spans every bucket and location filter, so the cell
    // holding Save hostage can be one the current filters do not render — and a message with no row in
    // it left the user with a permanently grey Save and nothing to look for.
    const firstIssue = useMemo(() => {
        if (!issue) return null;
        const label = rowLabels?.get(issue.rowId);
        return label ? `${issue.message} — ${label}` : issue.message;
    }, [issue, rowLabels]);

    const handleSave = useCallback(async () => {
        // The edits this request carries. The grids stay editable while it is out, so the store is told
        // what was submitted (`submit`) and clears exactly that (`settle`) — a cell typed in the meantime
        // stays pending instead of being dropped unsaved.
        const submitted = pending.edits;
        pending.submit(submitted);
        setIsSaving(true);

        try {
            const written = await saveAll(pending.payload());
            const writtenIds = new Set((written ?? []).map((config) => Number(config.id)));

            // Wait for the refetch before dropping the overlay. Discarding first left the grid rendering
            // the STALE cache with no pending marks for the whole round trip — indistinguishable from a
            // rejected save, and permanent if the refetch itself failed.
            //
            // Object form, like every other Budget hook: a PREFIX match, so it also reaches the
            // bucket-scoped keys ([BudgetBillingConfigs, 'global', bucketId]). v4 still accepts a
            // positional key, but v5 reads an array as a filters object with no queryKey — which matches
            // every query in the cache.
            await queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetBillingConfigs] });
            pending.settle(submitted, writtenIds);

            // A row the endpoint skipped (deleted by someone else since the grid loaded) keeps its edits.
            // Saying so is the point: the mutation has already toasted success.
            if (submitted.some((edit) => !writtenIds.has(edit.rowId))) {
                dispatchErrorNotification({ message: t('hint.someRowsNotSaved') });
            }
        } catch {
            // useUpdateRecord has toasted the server's message; nothing here is cleared.
            pending.settle(submitted, new Set());
        } finally {
            setIsSaving(false);
        }
    }, [saveAll, pending, dispatchErrorNotification, t]);

    return (
        <FormSaveActions
            variant="sticky"
            canSave={pending.isDirty && !isSaving && !firstIssue}
            isDirty={pending.isDirty}
            isSaving={isSaving}
            onSave={handleSave}
            onCancel={pending.discard}
            firstIssue={firstIssue ?? undefined}
            // The blocking row can be off screen behind a filter, and Verwerfen is all-or-nothing, so
            // the message carries the one escape that keeps the rest of the page's work.
            onDiscardIssue={issue ? () => pending.dropRow(issue.rowId) : undefined}
            pendingCount={pending.count}
            legend={<CellLegend cues={legendCues} />}
        />
    );
};

export default ConfigSaveBar;
